import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '@app/services/account.service';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

function syncNewPasswordMismatch(group: AbstractControl) {
  const p = group.get('newPassword')?.value ?? '';
  const c = group.get('confirmPassword')?.value ?? '';
  const confirm = group.get('confirmPassword');
  if (!confirm) {
    return null;
  }

  if (p && c && p !== c) {
    confirm.setErrors({ ...(confirm.errors ?? {}), mismatch: true });
  } else if (confirm.errors?.['mismatch']) {
    const { mismatch, ...rest } = confirm.errors;
    confirm.setErrors(Object.keys(rest).length ? rest : null);
  }
  return null;
}

@Component({
  selector: 'app-forgott-password-change',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidatedInputComponent,
  ],
  templateUrl: './forgott-password-change.component.html',
  styleUrl: './forgott-password-change.component.scss',
})
export class ForgottPasswordChangeComponent implements OnInit {
  form: FormGroup;
  errorMessage = '';
  loading = false;
  invalidLink = false;
  accountHint = '';

  private apiIdentifier = '';
  private apiToken = '';

  private accountService = inject(AccountService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);

  constructor(fb: FormBuilder) {
    this.form = fb.group(
      {
        newPassword: fb.nonNullable.control('', [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirmPassword: fb.nonNullable.control('', [Validators.required]),
      },
      { validators: [syncNewPasswordMismatch] },
    );
  }

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap;
    const tokenParam = q.get('token') ?? '';
    const hashid =
      q.get('hashid') ?? q.get('hashId') ?? '';

    if (tokenParam && hashid) {
      this.apiIdentifier = hashid;
      this.apiToken = tokenParam;
      this.accountHint = '';
    } else {
      const identifier =
        q.get('identifier') ?? q.get('email') ?? '';
      this.apiIdentifier = identifier;
      this.apiToken = tokenParam;
      this.accountHint = identifier;
    }

    this.invalidLink = !this.apiIdentifier || !this.apiToken;
  }

  onSubmit(): void {
    if (this.invalidLink || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const newPassword = this.form.getRawValue()['newPassword'];

    this.loading = true;
    this.errorMessage = '';

    this.accountService
      .resetPassword({
        identifier: this.apiIdentifier,
        token: this.apiToken,
        newPassword,
      })
      .subscribe({
        next: (response) => {
          if (response.requires2Fa) {
            this.loading = false;
            void this.router.navigate(['/auth/2fa'], {
              queryParams: this.route.snapshot.queryParams,
              state: {
                passwordReset: {
                  identifier: this.apiIdentifier,
                  token: this.apiToken,
                  newPassword,
                },
              },
            });
            return;
          }
          if (!response.success) {
            this.loading = false;
            this.errorMessage =
              response.errors?.join(' ') ??
              this.translate.instant('forgottenPasswordChange.resetFailed');
            return;
          }
          this.loading = false;
          void this.router.navigate(['/auth/login']);
        },
        error: (err: unknown) => {
          this.loading = false;
          this.errorMessage = this.formatHttpError(err);
        },
      });
  }

  cancel(): void {
    void this.router.navigate(['/auth/login']);
  }

  private formatHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && Array.isArray(body.errors)) {
        const e = body.errors as string[];
        if (e.length) {
          return e.join(' ');
        }
      }
    }
    return this.translate.instant('forgottenPasswordChange.resetFailed');
  }
}
